import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { Button, Modal, ModalDialogue } from '@sg/garnish';
import { Icon, IconLink, theme, useResponsive, WithTooltip } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

const CustomizationDietaryRestrictionsInfo = (
  props: DietaryRestrictionInfoProps,
) => {
  const { restrictionNotice, onRequestModal } = props;
  const { currentBreakpoint } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabel);

  // ─────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <IconLink
        name="IconAlert"
        color={theme.colors.CAUTION}
        accessibilityLabel={a11yLabel}
        onPress={onRequestModal}
        width={16}
        height={16}
      />
    );
  }

  return (
    <WithTooltip text={restrictionNotice} placement="left" triggerMode="press">
      <Icon
        name="IconAlert"
        color={theme.colors.CAUTION}
        width={16}
        height={16}
      />
    </WithTooltip>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const CustomizationDietaryRestrictionsModal = (
  props: DietaryRestrictionModalProps,
) => {
  const { visible, restrictionNotice, onRequestClose } = props;

  // ─────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabelModal);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue visible={visible} accessibilityLabel={a11yLabel}>
      <Modal.BodyText sizeMatch={['18']}>{restrictionNotice}</Modal.BodyText>

      <Modal.Footer withoutTopBorder>
        <Button
          palette="primaryOutline"
          size="large-wide"
          style={styles.button}
          onPress={onRequestClose}
        >
          <FormattedMessage {...messages.cta} />
        </Button>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    maxWidth: 180,
    alignSelf: 'center',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Open dietary restriction info',
    description: 'Customization | Dietary Restrictions | A11y',
  },
  a11yLabelModal: {
    defaultMessage: 'Dietary restrictions info',
    description: 'Customization | Dietary Restrictions Modal | A11y',
  },
  cta: {
    defaultMessage: 'Got it',
    description: 'Customization | Dietary Restrictions Modal | CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type DietaryRestrictionInfoProps = {
  restrictionNotice: string;
  onRequestModal: () => void;
};

type DietaryRestrictionModalProps = {
  visible: boolean;
  restrictionNotice: string;
  onRequestClose: () => void;
};

// ─── Compound Components ─────────────────────────────────────────────────────

export const CustomizationDietaryRestrictions = {
  Info: CustomizationDietaryRestrictionsInfo,
  Modal: CustomizationDietaryRestrictionsModal,
};
